import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Loader2, ShieldCheck, Smartphone, AlertCircle, CheckCircle2,
  XCircle, Search, Info, Globe, Server, Activity,
} from "lucide-react";
import api from "@/lib/api";

/**
 * SMS Diagnostics — Master-Admin-only.
 *
 * Wraps `POST /api/admin/security/sms-diagnostics`. Never surfaces
 * secrets: the backend already masks SIDs (`ACfd…`, `MGdd…`), redacts
 * phone digits (`+141***0200`) and NEVER returns the Twilio Auth Token
 * or the plaintext OTP. This page just renders what the backend gives us.
 */
function StatusPill({ status, label }) {
  const clsMap = {
    OK: "bg-emerald-50 text-emerald-700 border-emerald-200",
    WARN: "bg-amber-50 text-amber-800 border-amber-200",
    FAIL: "bg-rose-50 text-rose-700 border-rose-200",
    NEUTRAL: "bg-slate-50 text-slate-600 border-slate-200",
  };
  const IconMap = { OK: CheckCircle2, WARN: AlertCircle, FAIL: XCircle, NEUTRAL: Info };
  const Icon = IconMap[status] || Info;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${clsMap[status] || clsMap.NEUTRAL}`}
      data-testid="sms-diag-pill"
    >
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}

function pillForDelivery(status) {
  const s = (status || "").toUpperCase();
  if (["DELIVERED", "SMS_DELIVERED"].includes(s)) return "OK";
  if (["ACCEPTED", "SMS_ACCEPTED", "QUEUED", "SMS_QUEUED", "SENT", "SMS_SENT"].includes(s)) return "NEUTRAL";
  if (["FAILED", "SMS_FAILED", "UNDELIVERED", "SMS_UNDELIVERED", "BLOCKED"].includes(s)) return "FAIL";
  if (["EXPIRED", "SMS_EXPIRED"].includes(s)) return "WARN";
  return "NEUTRAL";
}

export default function SmsDiagnostics() {
  const { t } = useTranslation();
  const [username, setUsername] = useState("");
  const [busy, setBusy] = useState(false);
  const [diag, setDiag] = useState(null);
  const [err, setErr] = useState("");

  const run = async (e) => {
    e?.preventDefault?.();
    setErr("");
    setDiag(null);
    if (!username.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/security/sms-diagnostics", { username: username.trim() });
      setDiag(data);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Diagnostic failed.");
      toast.error(e2?.response?.data?.detail || "Diagnostic failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="sms-diagnostics">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          {t("sms_diag.title", { defaultValue: "SMS Diagnostics" })}
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          {t("sms_diag.subtitle", {
            defaultValue:
              "Inspect the exact status of the SMS delivery pipeline for any user. Detected country, sender pool, last Twilio attempts and error codes — without ever revealing the API key or OTP.",
          })}
        </p>
      </div>

      <form onSubmit={run} className="flex items-center gap-3 bg-white rounded-xl border border-slate-200 p-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder={t("sms_diag.placeholder", { defaultValue: "Enter a username, e.g. master1, agent2" })}
            className="w-full h-11 pl-10 pr-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-[#0F62FE]/60 text-sm"
            data-testid="sms-diag-username"
            autoComplete="off"
          />
        </div>
        <button
          type="submit"
          disabled={busy || !username.trim()}
          data-testid="sms-diag-run"
          className="h-11 px-5 rounded-lg bg-[#0F62FE] text-white font-semibold text-sm hover:bg-[#0050E6] disabled:opacity-60 inline-flex items-center gap-2"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
          {t("sms_diag.run", { defaultValue: "Run diagnostic" })}
        </button>
      </form>

      {err && (
        <div className="rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-sm p-3" data-testid="sms-diag-error">
          {err}
        </div>
      )}

      {diag && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Server className="w-4 h-4 text-slate-500" />
              <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider">
                {t("sms_diag.provider", { defaultValue: "SMS Provider" })}
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <Row label={t("sms_diag.provider_name", { defaultValue: "Provider" })} value={diag.provider?.provider} />
              <Row
                label={t("sms_diag.configured", { defaultValue: "Configured" })}
                value={diag.provider?.configured ? "Yes" : "No"}
                status={diag.provider?.configured ? "OK" : "FAIL"}
              />
              <Row label={t("sms_diag.account_sid", { defaultValue: "Account SID" })} value={diag.provider?.twilio_account_sid_prefix || "—"} mono />
              <Row label={t("sms_diag.msg_service_sid", { defaultValue: "Messaging Service SID" })} value={diag.provider?.twilio_messaging_service_sid_prefix || "—"} mono />
              <Row
                label={t("sms_diag.sender_pool", { defaultValue: "Sender pool" })}
                value={diag.sender_pool_reason || "—"}
                status={diag.sender_pool_ok ? "OK" : "FAIL"}
                mono
              />
              <Row label={t("sms_diag.mock_active", { defaultValue: "Mock mode" })} value={diag.provider?.mock_active ? "Yes" : "No"} status={diag.provider?.mock_active ? "WARN" : "OK"} />
            </div>
          </div>

          {diag.user && (
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center gap-2 mb-3">
                <Smartphone className="w-4 h-4 text-slate-500" />
                <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider">
                  {t("sms_diag.user_info", { defaultValue: "User & Phone" })}
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <Row label={t("sms_diag.username", { defaultValue: "Username" })} value={diag.user.username} />
                <Row label={t("sms_diag.role", { defaultValue: "Role" })} value={diag.user.role} />
                <Row label={t("sms_diag.masked_phone", { defaultValue: "Phone (masked)" })} value={diag.user.phone_masked || "—"} mono />
                <Row
                  label={t("sms_diag.e164_valid", { defaultValue: "E.164 valid (libphonenumber)" })}
                  value={diag.user.phone_e164_valid ? "Yes" : "No"}
                  status={diag.user.phone_e164_valid ? "OK" : "FAIL"}
                />
                <Row
                  label={t("sms_diag.country", { defaultValue: "Detected country" })}
                  value={diag.user.country_code || "—"}
                  icon={<Globe className="w-3 h-3 text-slate-400" />}
                />
              </div>
            </div>
          )}

          {diag.user?.recent_login_otps?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck className="w-4 h-4 text-slate-500" />
                <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider">
                  {t("sms_diag.recent", { defaultValue: "Last 3 SMS attempts" })}
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="text-left py-2 pr-3">{t("sms_diag.when", { defaultValue: "When" })}</th>
                      <th className="text-left py-2 pr-3">{t("sms_diag.status", { defaultValue: "Status" })}</th>
                      <th className="text-left py-2 pr-3">{t("sms_diag.twilio_status", { defaultValue: "Twilio" })}</th>
                      <th className="text-left py-2 pr-3">{t("sms_diag.err_code", { defaultValue: "Error" })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diag.user.recent_login_otps.map((r) => (
                      <tr key={r.id} className="border-b border-slate-100" data-testid={`sms-attempt-${r.id}`}>
                        <td className="py-2 pr-3 whitespace-nowrap text-slate-600">
                          {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                        </td>
                        <td className="py-2 pr-3">
                          <span className="text-slate-800 font-semibold">{r.status || "—"}</span>
                        </td>
                        <td className="py-2 pr-3">
                          <StatusPill status={pillForDelivery(r.sms_status)} label={r.sms_status || "—"} />
                        </td>
                        <td className="py-2 pr-3 text-slate-600 max-w-[280px] truncate" title={r.sms_error_message || ""}>
                          {r.sms_error_code
                            ? <><code className="text-rose-700 font-mono">{r.sms_error_code}</code> {r.sms_error_message}</>
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-[11px] text-slate-600 leading-relaxed flex gap-2">
            <Info className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
            <span>
              {t("sms_diag.note", {
                defaultValue:
                  "This page never displays the Twilio Auth Token, the plaintext OTP, or the full phone number. If a Twilio error code appears (e.g. 21408, 21610, 30003), consult the Twilio Console → Verify/Messaging → Debugger for the delivery trace.",
              })}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, status, mono = false, icon = null }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 border-b border-slate-100 last:border-0">
      <span className="text-slate-500 text-xs">{label}</span>
      <span className={`text-right flex items-center gap-2 ${mono ? "font-mono text-xs" : "text-sm"} text-slate-800`}>
        {icon}
        {status ? <StatusPill status={status} label={value} /> : value}
      </span>
    </div>
  );
}
